import ActionIconButton from '@components/Common/ActionIconButton';
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import DetailSection from '@components/Common/DetailSection';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import FormField from '@components/Common/FormField';
import StatTile from '@components/Common/StatTile';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import RecordLink from '@components/Common/RecordLink';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import {
  getReferralRewardsQuery,
  getReferredJobSeekersQuery,
  markReferralRewardsPaidMutation,
  myReferralsQuery,
  setMyReferralPayoutMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { fmtMoney } from '@helpers/money';
import { referralLink } from '@helpers/siteOrigin';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Box, Button, Checkbox, Chip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRememberListView } from '@helpers/listView';
import Draws from './Draws';
import { downloadCsv, fetchAllPayouts, payoutCsv } from './payoutExport';
import ReviewQueue from './ReviewQueue';
import {
  PERSON_STATUS_LABELS,
  REWARD_STATUS_LABELS,
  payoutLine,
  referredStatusLine,
  referrerLabel,
  referrerPath,
  rewardTitle,
  type ReferredSeekerRow,
  type RewardRow,
} from './review';
import './index.scss';
import { formatDate } from '@helpers/date';

interface ReferredPerson {
  name: string;
  joinedAt: string | null;
  qualified: boolean;
  /** False outside India: this person can never earn the referrer a reward. */
  eligible?: boolean | null;
  /** PENDING, UNDER_REVIEW, APPROVED, PAID or NOT_ELIGIBLE. */
  status?: string | null;
  /** One plain sentence: what they are waiting on, or why not. */
  statusReason?: string | null;
}

interface MyReferrals {
  code: string;
  /** What one qualified referral pays, in rupees. */
  amountPerReferral: number;
  currency: string;
  /** False outside India: the programme pays Indian users only, by UPI. */
  eligible: boolean | null;
  referred: number;
  qualified: number;
  earned: number;
  paid: number;
  /** Rewards waiting for a Super Admin, and their amount. Not counted in earned until approved. */
  underReview?: number | null;
  pendingAmount?: number | null;
  /** Whether referred people must verify their phone by a code to qualify. */
  phoneVerificationRequired?: boolean | null;
  people: ReferredPerson[];
  payoutUpiId: string | null;
  payoutPhone: string | null;
}

export { payoutLine, referredStatusLine } from './review';

const PAGE_SIZE = 50;
/*
 * Review first: nothing is payable until a Super Admin approves it, so the queue that blocks
 * money is the one the page opens on. "To pay" became "Approved, to pay" for the same reason: a
 * reward is no longer owed the moment someone qualifies.
 */
const REVIEW = 'PENDING_REVIEW';
const statusOptions = (toReview: number) => [
  { value: REVIEW, label: toReview ? `Review (${toReview})` : 'Review' },
  { value: 'EARNED', label: REWARD_STATUS_LABELS.EARNED },
  { value: 'PAID', label: REWARD_STATUS_LABELS.PAID },
  { value: 'REJECTED', label: REWARD_STATUS_LABELS.REJECTED },
  { value: '', label: 'All' },
];
const PAYOUT_COLUMNS = ['referrer', 'referred', 'amount', 'earned', 'status'];

/*
 * This read "Waiting for email verification", which was the old rule. A verified friend with an
 * empty profile now earns nothing, so saying that would tell an HR their referral counted when it
 * did not.
 */
export const personStatus = (p: ReferredPerson) => {
  // The backend's own status once it sends one; the rules below are what it said before it did.
  if (p.status) return PERSON_STATUS_LABELS[p.status] ?? p.status;
  if (p.qualified) return 'Qualified';
  // Someone outside India can never qualify; asking them for a profile chased nothing.
  if (p.eligible === false) return 'Outside India, not eligible for a reward';
  return 'Needs a resume and a complete profile';
};


/**
 * An HR's (or a Super Admin's) own link and the people who joined through it.
 *
 * `showPayout` is false for a Super Admin: they settle everyone else's rewards from the Payouts
 * table below and are not paid through this program, so "Where to pay you" is noise on their view.
 */
const MyLink: React.FC<{ showPayout: boolean }> = ({ showPayout }) => {
  const { showToast } = useToast();
  const [data, setData] = useState<MyReferrals | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchGraphQLApiData(myReferralsQuery())
      .then((r) => setData(r?.data?.myReferrals ?? null))
      .catch(() => setError(true));
  }, []);

  if (error) return <EmptyState compact title="Your referral link could not be loaded" body="Try again in a moment." />;
  if (!data) return <SkeletonLoader rows={4} />;

  const link = referralLink(data.code);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showToast('Link copied', 'success');
    } catch {
      showToast('Copy failed; select the link and copy it by hand.', 'error');
    }
  };
  const shareText = `Looking for work in jewellery or luxury retail? Sign up on Mintly with my link: ${link}`;

  return (
    <>
      {/* Neutral tiles; only "Under review" takes a colour, and only while something waits. */}
      <Box className="cc-stat-grid">
        <StatTile label="Joined" value={String(data.referred)} icon={<GroupAddOutlinedIcon />} />
        {/* "Qualified", not "Verified". Verification is no longer what earns anything, and a tile
            counting it would be the most prominent wrong number on the page. */}
        <StatTile label="Qualified" value={String(data.qualified)} icon={<HowToRegOutlinedIcon />} />
        <StatTile label="Earned" value={fmtMoney(data.earned, data.currency)} icon={<PaymentsOutlinedIcon />} />
        <StatTile label="Paid" value={fmtMoney(data.paid, data.currency)} icon={<PaidOutlinedIcon />} />
        <StatTile
          label={`Under review (${data.underReview ?? 0})`}
          value={fmtMoney(data.pendingAmount ?? 0, data.currency)}
          icon={<HourglassEmptyOutlinedIcon />}
          tone={(data.underReview ?? 0) > 0 ? 'warning' : 'neutral'}
        />
      </Box>

      <DetailSection title="Your referral link" icon={<ShareOutlinedIcon />} sx={{ mb: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {data.eligible === false
            ? 'Share it with candidates. Referral rewards are paid to users in India only, by UPI. Add an Indian mobile number to your profile, or save a UPI ID below, to start earning.'
            : data.amountPerReferral > 0
              ? `Share it with candidates in India. You earn ${fmtMoney(data.amountPerReferral, data.currency)} when someone you refer ${data.phoneVerificationRequired ? 'verifies their WhatsApp number, ' : ''}verifies their email, applies to a job, completes their profile with a readable resume and works in jewellery or luxury retail. Every reward is reviewed before payment. ${data.qualified} so far. Rewards are paid by hand to the UPI ID you save below.`
              : 'Share it with candidates. No reward is set yet, so referrals are tracked but earn nothing until a Super Admin sets one in Settings.'}
        </Typography>
        <Typography component="p" className="rf-link">{link}</Typography>
        <Box className="rf-actions">
          <Button variant="contained" startIcon={<ContentCopyOutlinedIcon />} onClick={copy}>Copy link</Button>
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Share on WhatsApp
          </Button>
        </Box>
      </DetailSection>

      {showPayout && <PayoutDetails upiId={data.payoutUpiId} phone={data.payoutPhone} />}

      <DetailSection title="Who joined through your link" icon={<GroupAddOutlinedIcon />} sx={{ mb: 0 }}>
        {/* Records, not a comparison: a person, their state and what they are waiting on. So
            MintlyTable, which turns each into a card on a phone, rather than MetricTable, which
            scrolled a sentence-long "What is next" sideways off a 390px screen. */}
        <MintlyTable
          rows={data.people}
          getRowKey={(p: ReferredPerson, i: number) => `${p.name}-${i}`}
          emptyLabel="Nobody has signed up through your link yet."
          selectors={['name', 'joined', 'status', 'reason']}
          columns={{
            name: { card: 'title', tablet: true },
            joined: { card: 'fact' },
            status: { card: 'fact', tablet: true },
            reason: { card: 'fact', tablet: true },
          }}
          tableHeadRow={{ name: 'Candidate', joined: 'Joined', status: 'Status', reason: 'What is next' }}
          tableBodyRow={{
            name: (p: ReferredPerson) => <>{p.name}</>,
            joined: (p: ReferredPerson) => <>{formatDate(p.joinedAt, '')}</>,
            status: (p: ReferredPerson) => <>{personStatus(p)}</>,
            reason: (p: ReferredPerson) => <>{p.statusReason ?? ''}</>,
          }}
        />
      </DetailSection>
    </>
  );
};

/** Where the signed-in HR wants their own rewards paid. Blank clears; the backend validates. */
const PayoutDetails: React.FC<{ upiId: string | null; phone: string | null }> = ({ upiId, phone }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({ upiId: upiId ?? '', phone: phone ?? '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await fetchGraphQLApiData(setMyReferralPayoutMutation(form.upiId.trim(), form.phone.trim()));
      const saved = r?.data?.setMyReferralPayout;
      setForm({ upiId: saved?.payoutUpiId ?? '', phone: saved?.payoutPhone ?? '' });
      showToast('Payout details saved', 'success');
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Not saved.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const set = (name: string, value: any) => setForm((f) => ({ ...f, [name]: String(value ?? '') }));
  return (
    <DetailSection title="Where to pay you" icon={<AccountBalanceWalletOutlinedIcon />} sx={{ mb: 0 }}>
      <Typography variant="body2" color="text.secondary">
        Rewards are paid to your UPI ID, or to this mobile number if you give no UPI ID. Only you and Super Admins see these.
      </Typography>
      <Box className="rf-payout">
        <FormField required={false} name="upiId" label="UPI ID" placeholder="name@bank" value={form.upiId} onChange={set} size="small" />
        <FormField required={false} name="phone" label="Mobile number" type="tel" placeholder="10 digits" value={form.phone} onChange={set} size="small" />
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Box>
    </DetailSection>
  );
};

const REFERRED_BY_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'HR', label: referrerLabel('HR') },
  { value: 'JOBSEEKER', label: referrerLabel('JOBSEEKER') },
  { value: 'AMBASSADOR', label: referrerLabel('AMBASSADOR') },
];
const QUALIFIED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Qualified' },
  { value: 'false', label: 'Not yet' },
];
const REFERRED_COLUMNS = ['candidate', 'joined', 'status', 'referrer', 'code'];

/**
 * Super Admin: every job seeker who joined through a referral link and who referred them.
 *
 * The payouts table below only shows people once five have qualified into a block, so someone
 * who had merely signed up through a link was visible nowhere.
 *
 * A row opens that person's referral workspace (/referrals/people/:jobSeekerId), because a table
 * row cannot hold every gate, the reward and its review. The filters and page live in the query
 * string under their own keys, so Back from the workspace returns to the same view and they do not
 * collide with anything else on this page.
 */
const ReferredPeople: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const referrerType = searchParams.get('filter_referred_by') ?? '';
  const qualifiedParam = searchParams.get('filter_qualified');
  const qualified: '' | 'true' | 'false' = qualifiedParam === 'true' || qualifiedParam === 'false' ? qualifiedParam : '';
  const applied = searchParams.get('referred_q') ?? '';
  const page = Math.max(parseInt(searchParams.get('referred_page') ?? '0', 10) || 0, 0);
  const [search, setSearch] = useState(applied);
  /** One writer for the four keys; any filter change starts again at the first page. */
  const setView = (next: { referrerType?: string; qualified?: string; applied?: string; page?: number }) => {
    const params = new URLSearchParams(searchParams);
    const put = (key: string, value: string) => (value ? params.set(key, value) : params.delete(key));
    if (next.referrerType !== undefined) put('filter_referred_by', next.referrerType);
    if (next.qualified !== undefined) put('filter_qualified', next.qualified);
    if (next.applied !== undefined) put('referred_q', next.applied);
    const p = next.page ?? 0;
    put('referred_page', p > 0 ? String(p) : '');
    setSearchParams(params, { replace: true });
  };
  const [rows, setRows] = useState<ReferredSeekerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(getReferredJobSeekersQuery(referrerType, qualified, applied, page, PAGE_SIZE))
      .then((r) => {
        if (!live) return;
        const p = r?.data?.getReferredJobSeekers;
        setRows(p?.items ?? []);
        setTotal(p?.total ?? 0);
      })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [referrerType, qualified, applied, page]);

  return (
    <DetailSection
      title="Referred job seekers"
      icon={<PersonSearchOutlinedIcon />}
      sx={{ mb: 0 }}
      headerAction={<Chip size="small" label={`${total.toLocaleString('en-IN')} total`} />}
    >
      <FilterRow>
        <SelectFormField required={false} name="referredBy" label="Referred by" value={referrerType}
          options={REFERRED_BY_OPTIONS} onChange={(_n, v) => setView({ referrerType: String(v) })} />
        <SelectFormField required={false} name="referredQualified" label="Status" value={qualified}
          options={QUALIFIED_OPTIONS} onChange={(_n, v) => setView({ qualified: String(v) })} />
        <FormField required={false} name="referredSearch" label="Search" placeholder="Name, email or code"
          value={search} onChange={(_n, v) => setSearch(String(v ?? ''))} size="small"
          textFieldProps={{ onKeyDown: (e) => { if (e.key === 'Enter') setView({ applied: search.trim() }); } }} />
        <Button variant="outlined" onClick={() => setView({ applied: search.trim() })}>Search</Button>
      </FilterRow>

      {error ? (
        <EmptyState compact title="Referred job seekers could not be loaded" body="Try again in a moment." />
      ) : loading && rows.length === 0 ? (
        <SkeletonLoader rows={6} columns={REFERRED_COLUMNS.length} />
      ) : rows.length === 0 ? (
        <EmptyState compact title="Nobody matches" body="Nobody has signed up through a referral link with these filters." />
      ) : (
        <MintlyTable
          pagination={{ enabled: true, page, rowsPerPage: PAGE_SIZE, totalNumberOfRows: total, handleChangePage: (_e, n) => setView({ page: n }) }}
          rows={rows}
          getRowKey={(r: ReferredSeekerRow) => r.jobSeekerId}
          onRowClick={(r: ReferredSeekerRow) => navigate(`/referrals/people/${r.jobSeekerId}`)}
          selectors={REFERRED_COLUMNS}
          tableHeadRow={{ candidate: <>Candidate</>, joined: <>Joined</>, status: <>Status</>, referrer: <>Referred by</>, code: <>Code</> }}
          tableBodyRow={{
            candidate: (r: ReferredSeekerRow) => (
              <span onClick={(e) => e.stopPropagation()}>
                <RecordLink to={`/jobseekers/${r.jobSeekerId}`}>{r.name || r.email || r.jobSeekerId}</RecordLink>
              </span>
            ),
            joined: (r: ReferredSeekerRow) => <>{formatDate(r.joinedAt, '')}</>,
            status: (r: ReferredSeekerRow) => (
              <>{referredStatusLine(r)}</>
            ),
            referrer: (r: ReferredSeekerRow) => (
              <>
                <span onClick={(e) => e.stopPropagation()}>
                  <RecordLink to={referrerPath(r)}>{r.referrerName || r.referrerEmail || r.referrerId}</RecordLink>
                </span>
                {` (${referrerLabel(r.referrerType)})`}
              </>
            ),
            code: (r: ReferredSeekerRow) => <span className="rf-code">{r.code}</span>,
          }}
        />
      )}
    </DetailSection>
  );
};

const PAYOUT_REFERRER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'JOBSEEKER', label: referrerLabel('JOBSEEKER') },
  { value: 'HR', label: referrerLabel('HR') },
  { value: 'AMBASSADOR', label: referrerLabel('AMBASSADOR') },
];
/* The payouts filters' own URL keys, so a filtered payout view can be bookmarked or linked to (the
   HR Ambassador page links its rewards under review here) and does not collide with the referred
   list's keys below. */
const PAYOUT_KEYS = { referrerType: 'filter_payout_referrer', referrerId: 'filter_payout_referrer_id', search: 'payout_q' } as const;
type PayoutFilters = { referrerType: string; referrerId: string; search: string };

const errorText = (e: any, fallback: string) =>
  String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || fallback;
const rewardsWord = (n: number) => (n === 1 ? '1 reward' : `${n} rewards`);

/**
 * Super Admin: the review queue, every reward, what is owed, and marking payouts done.
 *
 * One query feeds both views. The Review filter renders the evidence and the approve and reject
 * actions; every other filter is the payouts table, where only an approved reward can be paid.
 * "To pay" adds a checkbox per row so a payout run is one reference for many rewards, and the
 * whole filtered list (every page) exports as the CSV the payment is made from.
 */
const Payouts: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters: PayoutFilters = {
    referrerType: searchParams.get(PAYOUT_KEYS.referrerType) ?? '',
    referrerId: searchParams.get(PAYOUT_KEYS.referrerId) ?? '',
    search: searchParams.get(PAYOUT_KEYS.search) ?? '',
  };
  const { referrerType, referrerId, search: applied } = filters;
  const [search, setSearch] = useState(applied);
  const [status, setStatus] = useState(REVIEW);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<RewardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [outstanding, setOutstanding] = useState(0);
  const [toReview, setToReview] = useState({ count: 0, amount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  /** The rewards the payment-reference dialog is open for: one from a row, or the selection. */
  const [paying, setPaying] = useState<RewardRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  /** One writer for the filter keys; any change starts again at the first page. */
  const setFilters = (next: Partial<PayoutFilters>) => {
    const params = new URLSearchParams(searchParams);
    (Object.keys(next) as (keyof PayoutFilters)[]).forEach((k) => {
      const v = (next[k] ?? '').trim();
      if (v) params.set(PAYOUT_KEYS[k], v);
      else params.delete(PAYOUT_KEYS[k]);
    });
    setSearchParams(params, { replace: true });
    setPage(0);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(getReferralRewardsQuery(status, page, PAGE_SIZE, { referrerType, referrerId, search: applied }))
      .then((r) => {
        const p = r?.data?.getReferralRewards;
        setRows(p?.items ?? []);
        setTotal(p?.total ?? 0);
        setOutstanding(p?.outstanding ?? 0);
        setToReview({ count: p?.pendingReviewCount ?? 0, amount: p?.pendingReview ?? 0 });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status, page, referrerType, referrerId, applied]);

  useEffect(load, [load]);
  // A selection never survives the rows it was made on: after a reload or a page turn they are gone.
  useEffect(() => setSelected(new Set()), [rows]);

  const markPaid = async (reference?: string) => {
    if (!paying?.length) return;
    setBusy(true);
    try {
      const r = await fetchGraphQLApiData(markReferralRewardsPaidMutation(paying.map((p) => p.id), reference ?? ''));
      const n = Number(r?.data?.markReferralRewardsPaid ?? paying.length);
      showToast(paying.length === 1 ? 'Marked paid' : `Marked ${n} paid`, 'success');
      setPaying(null);
      load();
    } catch (e: any) {
      showToast(errorText(e, 'Not marked paid.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPayouts(fetchGraphQLApiData, status, { referrerType, referrerId, search: applied });
      downloadCsv(`referral-payouts-${(status || 'all').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`, payoutCsv(all));
      showToast(`Exported ${rewardsWord(all.length)}`, 'success');
    } catch (e: any) {
      showToast(errorText(e, 'Export failed.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const cur = rows[0]?.currency ?? 'INR';
  const reviewing = status === REVIEW;
  // Only EARNED (approved) rows can be paid, so only the To pay view selects.
  const selecting = status === 'EARNED';
  const payable = rows.filter((r) => r.status === 'EARNED');
  const chosen = payable.filter((r) => selected.has(r.id));
  const allOnPage = payable.length > 0 && chosen.length === payable.length;
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(payable.map((r) => r.id)));
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const chosenTotal = chosen.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const columns = selecting ? ['select', ...PAYOUT_COLUMNS] : PAYOUT_COLUMNS;

  const payingBody = () => {
    if (!paying?.length) return '';
    if (paying.length > 1) {
      const sum = paying.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      return `${rewardsWord(paying.length)}, ${fmtMoney(sum, paying[0].currency)} in all, will be marked paid with one reference. Enter the UPI or bank reference of the payment you made.`;
    }
    const p = paying[0];
    return `${fmtMoney(p.amount, p.currency)} to ${p.referrerName || p.referrerEmail || 'this person'}${payoutLine(p) ? ` (${payoutLine(p)})` : ', who has not given payout details'}. Enter the UPI or bank reference of the payment you made.`;
  };

  return (
    <DetailSection
      title="Payouts"
      icon={<PaymentsOutlinedIcon />}
      sx={{ mb: 0 }}
      headerAction={
        <Box className="rf-chips">
          <Chip size="small" label={`${toReview.count} to review, ${fmtMoney(toReview.amount, cur)}`} />
          <Chip size="small" label={`${fmtMoney(outstanding, cur)} to pay`} />
        </Box>
      }
    >
      <FilterRow>
        <SelectFormField
          required={false}
          name="payoutStatus"
          label="Show"
          value={status}
          options={statusOptions(toReview.count)}
          onChange={(_n, v) => { setStatus(String(v)); setPage(0); }}
        />
        <SelectFormField
          required={false}
          name="payoutReferrer"
          label="Referrer"
          value={referrerType}
          options={PAYOUT_REFERRER_OPTIONS}
          onChange={(_n, v) => setFilters({ referrerType: String(v ?? '') })}
        />
        <FormField
          required={false}
          name="payoutSearch"
          label="Search"
          placeholder="Name, email or code"
          value={search}
          onChange={(_n, v) => setSearch(String(v ?? ''))}
          size="small"
          textFieldProps={{ onKeyDown: (e) => { if (e.key === 'Enter') setFilters({ search }); } }}
        />
        <Box className="rf-toolbar">
          <ActionIconButton label="Search payouts" icon={<SearchOutlinedIcon />} onClick={() => setFilters({ search })} />
          <ActionIconButton
            label="Export CSV (every page of this view)"
            icon={<FileDownloadOutlinedIcon />}
            busy={exporting}
            disabled={total === 0}
            onClick={exportCsv}
          />
        </Box>
      </FilterRow>

      {referrerId && (
        <Box className="rf-chips rf-payout-scope">
          <Chip
            size="small"
            label={`One ${referrerType ? referrerLabel(referrerType) : 'referrer'} only`}
            onDelete={() => setFilters({ referrerId: '' })}
          />
        </Box>
      )}

      {selecting && payable.length > 0 && (
        <FilterRow>
          <Box className="rf-review__select-all">
            <Checkbox
              size="small"
              checked={allOnPage}
              indeterminate={!allOnPage && chosen.length > 0}
              onChange={toggleAll}
              slotProps={{ input: { 'aria-label': 'Select every reward on this page' } }}
            />
            <Typography variant="body2">
              {chosen.length ? `${chosen.length} selected, ${fmtMoney(chosenTotal, chosen[0].currency)}` : 'Select all'}
            </Typography>
          </Box>
          <ActionIconButton
            label="Mark selected paid"
            icon={<PriceCheckOutlinedIcon />}
            color="primary"
            disabled={!chosen.length}
            onClick={() => setPaying(chosen)}
          />
        </FilterRow>
      )}

      {error ? (
        <EmptyState compact title="Payouts could not be loaded" body="Try again in a moment." />
      ) : loading && rows.length === 0 ? (
        <SkeletonLoader rows={6} columns={PAYOUT_COLUMNS.length} />
      ) : reviewing ? (
        <ReviewQueue
          rows={rows}
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPage={setPage}
          onChanged={load}
          payoutLine={payoutLine}
        />
      ) : rows.length === 0 ? (
        <EmptyState compact title={status === 'EARNED' ? 'Nothing to pay' : 'No rewards here yet'} />
      ) : (
        <Box className="rf-payouts">
          <MintlyTable
            pagination={{ enabled: true, page, rowsPerPage: PAGE_SIZE, totalNumberOfRows: total, handleChangePage: (_e, n) => setPage(n) }}
            rows={rows}
            getRowKey={(r: RewardRow) => r.id}
            selectors={columns}
            columns={{
              select: { card: 'action', tablet: true },
              referrer: { card: 'title', tablet: true },
              referred: { card: 'subtitle' },
              amount: { card: 'fact', tablet: true },
              earned: { card: 'fact' },
              status: { card: 'status', tablet: true },
            }}
            tableHeadRow={{ select: <></>, referrer: <>Pay to</>, referred: <>For</>, amount: <>Amount</>, earned: <>Earned</>, status: <>Status</> }}
            tableBodyRow={{
              select: (r: RewardRow) =>
                r.status === 'EARNED' ? (
                  <Checkbox
                    size="small"
                    checked={selected.has(r.id)}
                    onChange={() => toggleOne(r.id)}
                    slotProps={{ input: { 'aria-label': `Select ${r.referrerName || r.referrerEmail || r.id}, ${fmtMoney(r.amount, r.currency)}` } }}
                  />
                ) : null,
              referrer: (r: RewardRow) => (
                <>
                  <RecordLink to={referrerPath(r)}>{r.referrerName || r.referrerEmail || r.referrerId}</RecordLink>
                  {` (${referrerLabel(r.referrerType)})`}
                  <Typography component="span" variant="body2" color="text.secondary" className="rf-payout-line">
                    {payoutLine(r) ?? 'No payout details given'}
                  </Typography>
                </>
              ),
              /*
               * The person this row pays for, linked. Money is about to move, so the row has to show
               * who it is for rather than a count: checking a payment against a number is not
               * checking it. It used to list the five people in a block; one row is one person now,
               * and the Block column went with it.
               */
              referred: (r: RewardRow) =>
                r.referredId ? (
                  <RecordLink to={`/jobseekers/${r.referredId}`}>
                    {r.referredName || r.referredId}
                  </RecordLink>
                ) : (
                  // A milestone or a draw prize is not for one person: say what it is for instead.
                  <>{rewardTitle(r)}</>
                ),
              amount: (r: RewardRow) => <>{fmtMoney(r.amount, r.currency)}</>,
              earned: (r: RewardRow) => <>{formatDate(r.earnedAt, '')}</>,
              /*
               * Only EARNED (approved) carries Mark paid: the backend refuses to pay a reward still
               * under review, and a button that always fails is worse than none.
               */
              status: (r: RewardRow) =>
                r.status === 'EARNED' ? (
                  <Button size="small" variant="outlined" onClick={() => setPaying([r])}>Mark paid</Button>
                ) : r.status === 'PAID' ? (
                  <>{`Paid${r.paymentRef ? `, ref ${r.paymentRef}` : ''}`}</>
                ) : r.status === 'REJECTED' ? (
                  <>
                    <CRMStatusChip status="declined" label={REWARD_STATUS_LABELS.REJECTED} />
                    {r.rejectedReason && (
                      <Typography component="span" variant="body2" color="text.secondary" className="rf-payout-line">
                        {r.rejectedReason}
                      </Typography>
                    )}
                  </>
                ) : r.status === 'COVERED' ? (
                  <CRMStatusChip status="inactive" label={REWARD_STATUS_LABELS.COVERED} />
                ) : (
                  <CRMStatusChip status="needsreview" label={REWARD_STATUS_LABELS[r.status] ?? r.status} />
                ),
            }}
          />
        </Box>
      )}

      <ConfirmDialog
        open={!!paying?.length}
        title={paying && paying.length > 1 ? `Mark ${paying.length} rewards paid?` : 'Mark this reward paid?'}
        body={payingBody()}
        reasonLabel="Payment reference"
        reasonPlaceholder="UPI or bank transaction id"
        actionLabel="Mark paid"
        actionColor="primary"
        busy={busy}
        onCancel={() => setPaying(null)}
        onConfirm={markPaid}
      />
    </DetailSection>
  );
};

/**
 * Referrals. Every HR (and Super Admin) has a link to share with candidates and sees who joined
 * through it; a Super Admin also sees every reward and marks payouts done. Rewards are set in
 * Settings; the rules are the backend's ReferralService.
 */
const Referrals: React.FC = () => {
  const superAdmin = isSuperAdmin(useCurrentUserRole());
  useRememberListView('/referrals');
  return (
    <CRMPageTemplate icon={<GroupAddOutlinedIcon />} title="Referrals">
      <Box className="rf-page">
        <MyLink showPayout={!superAdmin} />
        {/* Payouts above the referred list: its Review queue is what holds money back. */}
        {superAdmin && <Payouts />}
        {superAdmin && <Draws />}
        {superAdmin && <ReferredPeople />}
      </Box>
    </CRMPageTemplate>
  );
};

export default Referrals;
